import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import LogInModal from 'flarum/forum/components/LogInModal';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';

import { apiUrl, coverStyle } from '../../common/clubs';

/**
 * /kulupler — every club, so people outside one can find it and ask to join.
 * Members go straight to the club's discussion, which is where the club lives.
 */
export default class ClubsIndexPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.loading = true;
    this.clubs = [];
    this.busy = {};

    const title = extractText(app.translator.trans('happydigital-clubs.forum.index.title'));

    // Core pages register themselves in app.history; the mobile header only
    // shows a back button (instead of the drawer button) when they do.
    app.history.push('happydigital-clubs.index', title);
    app.setTitle(title);
    app.setTitleCount(0);

    this.load();
  }

  load() {
    app
      .request({ method: 'GET', url: apiUrl() })
      .then((response) => {
        this.clubs = response.data || [];
      })
      .catch(() => {})
      .then(() => {
        this.loading = false;
        m.redraw();
      });
  }

  view() {
    return (
      <div className="ClubsIndexPage">
        <header className="Hero ClubsIndexPage-hero">
          <div className="container">
            <div className="containerNarrow">
              <h1 className="Hero-title">{app.translator.trans('happydigital-clubs.forum.index.title')}</h1>
              <div className="Hero-subtitle">{app.translator.trans('happydigital-clubs.forum.index.subtitle')}</div>
            </div>
          </div>
        </header>

        <div className="container">{this.content()}</div>
      </div>
    );
  }

  content() {
    if (this.loading) return <LoadingIndicator />;

    if (!this.clubs.length) {
      return <Placeholder text={app.translator.trans('happydigital-clubs.forum.index.empty')} />;
    }

    return <ul className="ClubsIndexPage-list">{this.clubs.map((club) => <li key={club.id}>{this.card(club)}</li>)}</ul>;
  }

  card(club) {
    return (
      <div className="ClubCard">
        <div className={classList('ClubCard-cover', { 'ClubCard-cover--image': !!club.coverUrl })} style={coverStyle(club)}>
          {club.coverUrl ? null : icon(club.icon || 'fas fa-users')}
        </div>

        <div className="ClubCard-body">
          <h3 className="ClubCard-title">{club.name}</h3>
          <div className="ClubCard-meta">{app.translator.trans('happydigital-clubs.forum.club.member_count', { count: club.memberCount })}</div>
          {club.description ? <p className="ClubCard-description">{club.description}</p> : null}
          <div className="ClubCard-actions">{this.action(club)}</div>
        </div>
      </div>
    );
  }

  action(club) {
    const viewer = club.viewer || {};
    const busy = !!this.busy[club.id];

    if (viewer.isMember) {
      return LinkButton.component(
        { className: 'Button Button--primary', icon: 'fas fa-comments', href: app.route('discussion', { id: club.discussionId }) },
        app.translator.trans('happydigital-clubs.forum.club.open')
      );
    }

    if (!app.session.user) {
      return Button.component(
        { className: 'Button', icon: 'fas fa-sign-in-alt', onclick: () => app.modal.show(LogInModal) },
        app.translator.trans('happydigital-clubs.forum.club.log_in_to_join')
      );
    }

    if (viewer.hasRequested) {
      return [
        <span className="ClubCard-status">{app.translator.trans('happydigital-clubs.forum.club.requested')}</span>,
        Button.component(
          { className: 'Button Button--link', loading: busy, onclick: () => this.membership(club, 'DELETE') },
          app.translator.trans('happydigital-clubs.forum.club.cancel_request')
        ),
      ];
    }

    if (viewer.canJoin) {
      return Button.component(
        { className: 'Button Button--primary', icon: 'fas fa-user-plus', loading: busy, onclick: () => this.membership(club, 'POST') },
        app.translator.trans('happydigital-clubs.forum.club.request_join')
      );
    }

    return null;
  }

  membership(club, method) {
    this.busy[club.id] = true;

    app
      .request({ method, url: apiUrl('/' + club.id + '/membership') })
      .then((response) => {
        this.clubs = this.clubs.map((item) => (item.id === club.id ? response.data : item));
      })
      .catch(() => {})
      .then(() => {
        delete this.busy[club.id];
        m.redraw();
      });
  }
}
